import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, ArrowRight, Loader2, AlertCircle, Instagram, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import GenerationNotificationModal from '../components/GenerationNotificationModal';

export const PilotoAutomatico = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMarketingPlan, setHasMarketingPlan] = useState(false);
  const [hasInstagramConnected, setHasInstagramConnected] = useState(false); // Default to false; will update after check
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [lastGenDate, setLastGenDate] = useState<string | null>(null);
  // Removed isAgentOn state (not used in UI)

  useEffect(() => {
    checkMarketingPlan();
    checkInstagramConnection();
  }, []);

  useEffect(() => {
    // Debug current gating state
    console.log('[PilotoAutomatico] state:', { hasMarketingPlan, hasInstagramConnected, lastGenDate });
  }, [hasMarketingPlan, hasInstagramConnected, lastGenDate]);

  // Navigate to calendar only when all conditions are met (including initial generation already happened)
  useEffect(() => {
    if (!loading && hasMarketingPlan && hasInstagramConnected && lastGenDate !== null) {
      navigate('/redes-sociales/calendario');
    }
  }, [loading, hasMarketingPlan, hasInstagramConnected, lastGenDate, navigate]);

  const checkInstagramConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      // Check if instagram_data table exists and has entries for this company
      const { data } = await supabase
        .schema((profile as any).id_company)
        .from('instagram_data')
        .select('id')
        .limit(1);

      // If there's no error but data is empty array or null, Instagram is not connected
      setHasInstagramConnected(!!(data && data.length > 0));
    } catch (err) {
      console.error('Error checking Instagram connection:', err);
      // If there's an error (like table doesn't exist), assume Instagram is not connected
      setHasInstagramConnected(false);
    }
  };

  const checkMarketingPlan = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      // Get company plan to gate feature for free plan
      const { data: company, error: companyError } = await supabase
        .from('company_profiles')
        .select('plan')
        .eq('id_company', (profile as any).id_company)
        .single();
      if (companyError) throw companyError;
      setIsFreePlan(company?.plan === 'gratis');

      // Check if marketing plan exists
      const { data: configData, error: configError } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .select('media_agent_analysis, media_agent_on, last_auto_gen_date')
        .eq('id', 0)
        .single();

      if (configError) throw configError;

      if (configData) {
        setLastGenDate(configData.last_auto_gen_date ?? null);
        if (configData.media_agent_analysis) {
          setHasMarketingPlan(true);
          // Navigation handled by effect once IG connection is confirmed
        }
      }
    } catch (err) {
      console.error('Error checking marketing plan:', err);
      setError(err instanceof Error ? err.message : 'Error checking marketing plan');
    } finally {
      setLoading(false);
    }
  };

  const handleStartInitialGeneration = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // Ensure agent is ON
      const { data: cfg } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .select('media_agent_on')
        .eq('id', 0)
        .single();

      if (!cfg?.media_agent_on) {
        const { error: updateError } = await supabase
          .schema((profile as any).id_company)
          .from('config')
          .update({ media_agent_on: true })
          .eq('id', 0);
        if (updateError) throw updateError;
      }

      // Set one-time session flag BEFORE triggering webhook so router allows access immediately
      try {
        window.sessionStorage.setItem('media_initial_generation_started', 'true');
      } catch {}
      // Also set a localStorage flag so Calendar shows the generation message upon arrival
      try {
        window.localStorage.setItem('calendar_show_generation_message', 'true');
      } catch {}
      // Notify router to re-check immediately
      try {
        window.dispatchEvent(new Event('media-initial-gen-started'));
      } catch {}

      // Trigger initial generation
      await fetch('https://ai-agency-dt7d.onrender.com/webhook/ecdac889-7227-4a9a-8f5e-1e4786573968', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, mode: 'start' })
      });

    } catch (err) {
      console.error('Error starting initial generation:', err);
      setError(err instanceof Error ? err.message : 'Error starting generation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto min-h-screen flex flex-col justify-center">
      {/* Notification Modal for initial generation */}
      <GenerationNotificationModal 
        isOpen={showNotificationModal} 
        onClose={() => {
          setShowNotificationModal(false);
          try { window.dispatchEvent(new Event('calendar-recheck')); } catch {}
          navigate('/redes-sociales/calendario');
        }}
      />
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="mb-6">
          <Rocket className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-3xl font-semibold text-gray-900 mb-3">
            Conocé al Manager de Redes IA
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
            Dejá que el manager de redes de Atlas te ayude a planear, generar y subir contenido a tus redes
          </p>
        </div>

        {/* Instagram connection modal */}
        {showInstagramModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
              <button 
                onClick={() => setShowInstagramModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center justify-center mb-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Instagram className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-center mb-2">Conexión de Instagram requerida</h3>
              
              <p className="text-gray-600 text-center mb-6">
                Para poder publicar el contenido generado, primero debes conectar la cuenta de Instagram de la empresa a Atlas.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setShowInstagramModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                
                <button
                  onClick={() => {
                    setShowInstagramModal(false);
                    navigate('/configuracion', { state: { section: 'instagram' } }); // Navigate to Settings page with instagram section selected
                  }}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
                >
                  <Instagram className="w-4 h-4" />
                  Conectar Instagram
                </button>
              </div>
            </div>
          </div>
        )}

        {isFreePlan ? (
          <div className="flex flex-col items-center gap-3">
            <div className="px-4 py-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-md">
              Esta función solo está disponible para planes pagos.
            </div>
            <button
              onClick={() => navigate('/subscription')}
              className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Seleccionar plan
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              if (!hasInstagramConnected) {
                setShowInstagramModal(true);
              } else if (lastGenDate === null) {
                setShowNotificationModal(true);
                void handleStartInitialGeneration();
              } else {
                navigate('/redes-sociales/plan-marketing-view');
              }
            }}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors inline-flex items-center gap-2 text-base"
          >
            Comenzar
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PilotoAutomatico;